import { useState } from 'react'
import { passkeysSupported, pinSignIn, register, signIn } from '../../lib/auth'
import { t } from '../../lib/i18n'
import { go } from '../../lib/classroom'
import { w } from '../../lib/words'
import Icon from '../Icon'
import Pichi from './Pichi'

/**
 * The front door. A student arrives here from the classroom QR code
 * (/join/CODE), makes an account with a passkey — no password to forget — and
 * lands in the class. (The owner account is claimed; there is no setup-token
 * field. A new owner is made by the current owner in the admin console.)
 */
export default function Welcome({ joinCode, onSignedIn }: { joinCode: string | null; onSignedIn: () => Promise<void> }) {
  const [mode, setMode] = useState<'up' | 'in' | 'pin'>('up')
  const [pin, setPin] = useState('')
  const [name, setName] = useState('')
  const [handle, setHandle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await onSignedIn()
    } catch (err) {
      const n = (err as { name?: string }).name
      setError(n === 'NotAllowedError' || n === 'AbortError' ? t('auth.cancelled') : (err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  // Suggest a username from the name, so a kid doesn't have to invent one.
  const suggest = (n: string) => n.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '').slice(0, 20)

  return (
    <div className="welcome">
      <div className="hero">
        <Pichi size={150} stage={2} mood="wave" />
        <p className="tagline">{w('tagline')}</p>
        <h1>Adulting <b>101</b></h1>
        <p className="muted">{w('welcomeBody')}</p>
        <p><button className="link" onClick={() => go('/guia')}><Icon name="book" size={16} /> {w('guideLink')}</button></p>
      </div>

      {mode === 'pin' ? (
        <section className="card">
          <h2><Icon name="lock" /> {w('pinSignIn')}</h2>
          <p className="muted small">{w('pinSignInHint')}</p>
          <label>
            {w('username')}
            <input
              value={handle}
              onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/\s/g, ''))}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </label>
          <label>
            {w('pin')}
            <input
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
              type="password"
              inputMode="numeric"
              autoComplete="current-password"
            />
          </label>
          <button
            className="primary big"
            disabled={busy || !handle || pin.length < 4}
            onClick={() => run(() => pinSignIn(handle, pin))}
          >
            <Icon name="lock" /> {busy ? '…' : w('signIn')}
          </button>
          {error && <p className="error" role="alert">{error}</p>}
          {passkeysSupported() && (
            <p className="muted small"><button className="link" onClick={() => { setMode('in'); setError(null) }}>{w('backToPasskey')}</button></p>
          )}
        </section>
      ) : !passkeysSupported() ? (
        <section className="card">
          <p>{t('auth.noPasskeySupport')}</p>
          <p><button className="link" onClick={() => setMode('pin')}>{w('pinSignIn')}</button></p>
        </section>
      ) : (
        <section className="card">
          {joinCode && <p className="join-note"><Icon name="people" /> {w('joiningClass')} <strong>{joinCode}</strong></p>}
          <div className="mode-switch" role="tablist">
            <button role="tab" aria-selected={mode === 'up'} className={mode === 'up' ? 'on' : ''} onClick={() => setMode('up')}>
              <Icon name="sparkle" /> {w('newHere')}
            </button>
            <button role="tab" aria-selected={mode === 'in'} className={mode === 'in' ? 'on' : ''} onClick={() => setMode('in')}>
              <Icon name="key" /> {w('haveOne')}
            </button>
          </div>

          {mode === 'up' && (
            <label>
              {w('yourName')}
              <input
                value={name}
                autoComplete="given-name"
                onChange={(e) => {
                  const v = e.target.value
                  if (!handle || handle === suggest(name)) setHandle(suggest(v))
                  setName(v)
                }}
              />
            </label>
          )}
          <label>
            {w('username')} <span className="muted small">— {w('usernameHint')}</span>
            <input
              value={handle}
              onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/\s/g, ''))}
              autoComplete="username webauthn"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </label>

          <button
            className="primary big"
            disabled={busy || (mode === 'up' && (!handle || !name.trim()))}
            onClick={() => run(() => (mode === 'in'
              ? signIn(handle || undefined)
              : register({ handle, displayName: name.trim() })))}
          >
            <Icon name="key" /> {busy ? '…' : mode === 'up' ? w('signUp') : w('signIn')}
          </button>
          {error && <p className="error" role="alert">{error}</p>}

          <p className="muted small">{w('passkeyNote')}</p>
          <p className="small"><button className="link" onClick={() => { setMode('pin'); setError(null) }}><Icon name="lock" size={14} /> {w('pinSignIn')}</button></p>
        </section>
      )}
    </div>
  )
}
